// ./src/hooks/useGpsTracker.ts
//
// Low-level hook that wraps the browser's Geolocation API.
// Produces raw GPS readings filtered by accuracy threshold.
//
// Responsibilities:
// - Start/stop watchPosition based on `enabled` prop
// - Discard readings with accuracy worse than threshold
// - Normalize GeolocationPosition into a flat GpsReading object
// - Surface permission and availability errors via onError callback
// - Clean up watcher on disable or unmount
//
// This hook does NOT:
// - Apply smart filtering (that's useTrackingFilter)
// - Accumulate distance (that's the session manager)
// - Buffer or upload anything (that's useTrackingUploader)

import { useEffect, useRef, useCallback } from "react";
import { GPS_ACCURACY_THRESHOLD, GPS_WATCHER_OPTIONS } from "@/config/trackingConstants";

// ============================================
// Types
// ============================================

/**
 * A normalized GPS reading from the Geolocation API.
 * All fields come directly from GeolocationPosition.coords
 * except `timestamp` which comes from GeolocationPosition.timestamp.
 */
export interface GpsReading {
  /** Latitude in degrees (-90 to 90) */
  lat: number;
  /** Longitude in degrees (-180 to 180) */
  lng: number;
  /** Altitude in meters above sea level, or null if unavailable */
  altitude: number | null;
  /** Speed in m/s from GPS, or null if unavailable */
  speed: number | null;
  /** Heading in degrees (0–360), or null if stationary/unavailable */
  heading: number | null;
  /** GPS accuracy in meters (radius of 68% confidence circle) */
  accuracy: number;
  /** Timestamp in milliseconds since epoch (from the device clock) */
  timestamp: number;
}

/**
 * Error categories for GPS failures.
 * Mapped from GeolocationPositionError.code plus custom cases.
 */
export type GpsErrorType =
  | "PERMISSION_DENIED"
  | "POSITION_UNAVAILABLE"
  | "TIMEOUT"
  | "NOT_SUPPORTED";

export interface GpsError {
  type: GpsErrorType;
  message: string;
}

export interface UseGpsTrackerProps {
  /** Whether the GPS watcher should be active */
  enabled: boolean;
  /**
   * Discard readings with accuracy worse than this value (meters).
   * @default 30 (from GPS_ACCURACY_THRESHOLD)
   */
  accuracyThreshold?: number;
  /**
   * Called for each GPS reading that passes the accuracy filter.
   * Must be a stable reference (wrap in useCallback or use a ref).
   */
  onReading: (reading: GpsReading) => void;
  /**
   * Called when the Geolocation API reports an error.
   * Must be a stable reference.
   */
  onError: (error: GpsError) => void;
}

// ============================================
// Helpers
// ============================================

/**
 * Map GeolocationPositionError.code to our GpsErrorType.
 */
const mapGeolocationError = (error: GeolocationPositionError): GpsError => {
  switch (error.code) {
    case error.PERMISSION_DENIED:
      return {
        type: "PERMISSION_DENIED",
        message: "Location permission denied. Please enable location access in your browser settings.",
      };
    case error.POSITION_UNAVAILABLE:
      return {
        type: "POSITION_UNAVAILABLE",
        message: "GPS position unavailable. Make sure location services are enabled on your device.",
      };
    case error.TIMEOUT:
      return {
        type: "TIMEOUT",
        message: "GPS position request timed out. Retrying...",
      };
    default:
      return {
        type: "POSITION_UNAVAILABLE",
        message: error.message || "Unknown geolocation error",
      };
  }
};

/**
 * Convert a GeolocationPosition to our flat GpsReading format.
 */
const toGpsReading = (position: GeolocationPosition): GpsReading => ({
  lat: position.coords.latitude,
  lng: position.coords.longitude,
  altitude: position.coords.altitude,
  speed: position.coords.speed,
  heading: position.coords.heading,
  accuracy: position.coords.accuracy,
  timestamp: position.timestamp,
});

// ============================================
// Hook
// ============================================

/**
 * Wraps navigator.geolocation.watchPosition with accuracy filtering
 * and clean lifecycle management.
 *
 * When `enabled` is true, starts watching. When false, stops.
 * On unmount, always cleans up. Readings with accuracy worse than
 * the threshold are silently discarded.
 *
 * @example
 * ```tsx
 * const handleReading = useCallback((reading: GpsReading) => {
 *   // Feed into filter, accumulate distance, etc.
 * }, []);
 *
 * const handleError = useCallback((error: GpsError) => {
 *   if (error.type === 'PERMISSION_DENIED') {
 *     showPermissionDialog();
 *   }
 * }, []);
 *
 * useGpsTracker({
 *   enabled: isTracking,
 *   onReading: handleReading,
 *   onError: handleError,
 * });
 * ```
 */
export const useGpsTracker = ({
  enabled,
  accuracyThreshold = GPS_ACCURACY_THRESHOLD,
  onReading,
  onError,
}: UseGpsTrackerProps): void => {
  // Store callbacks in refs to avoid restarting the watcher when they change.
  // The watcher lifecycle should only depend on `enabled`.
  const onReadingRef = useRef(onReading);
  onReadingRef.current = onReading;

  const onErrorRef = useRef(onError);
  onErrorRef.current = onError;

  const accuracyThresholdRef = useRef(accuracyThreshold);
  accuracyThresholdRef.current = accuracyThreshold;

  // Track the active watcher ID for cleanup
  const watchIdRef = useRef<number | null>(null);

  /**
   * Stop the active watcher if one exists.
   */
  const stopWatcher = useCallback(() => {
    if (watchIdRef.current !== null) {
      navigator.geolocation.clearWatch(watchIdRef.current);
      watchIdRef.current = null;
    }
  }, []);

  /**
   * Start/stop the geolocation watcher based on `enabled`.
   *
   * Dependencies: [enabled] only. Callback refs ensure we always
   * call the latest onReading/onError without restarting the watcher.
   */
  useEffect(() => {
    // Always stop any existing watcher first (handles enabled toggling)
    stopWatcher();

    if (!enabled) {
      return;
    }

    // Check browser support
    if (!navigator.geolocation) {
      onErrorRef.current({
        type: "NOT_SUPPORTED",
        message: "Geolocation API is not supported by this browser.",
      });
      return;
    }

    // Start watching
    const id = navigator.geolocation.watchPosition(
      (position) => {
        const reading = toGpsReading(position);

        // Discard inaccurate readings
        if (reading.accuracy > accuracyThresholdRef.current) {
          return;
        }

        onReadingRef.current(reading);
      },
      (error) => {
        onErrorRef.current(mapGeolocationError(error));
      },
      GPS_WATCHER_OPTIONS
    );

    watchIdRef.current = id;

    // Cleanup on disable or unmount
    return () => {
      stopWatcher();
    };
  }, [enabled, stopWatcher]);

  // Safety: also clean up on unmount regardless of effect cleanup
  useEffect(() => {
    return () => {
      stopWatcher();
    };
  }, [stopWatcher]);
};